import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Ticket, Crown, CreditCard, Minus, Plus, Tag } from "lucide-react";
import { motion } from "framer-motion";

export default function Tickets() {
    const [tiers, setTiers] = useState([]);
    const [games, setGames] = useState([]);
    const [params] = useSearchParams();
    const presetGameId = params.get("game") || "";

    const [selectedTier, setSelectedTier] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [gameId, setGameId] = useState(presetGameId);
    const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [promo, setPromo] = useState("");
    const [discount, setDiscount] = useState(null); // {code, discount_cents}
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        api.get("/ticket-tiers").then((r) => setTiers(r.data));
        api.get("/games?upcoming=true").then((r) => setGames(r.data));
    }, []);

    // Recalculate discount whenever tier or quantity changes (or clear if no longer valid)
    const subtotalCents = selectedTier ? Math.round(selectedTier.price_usd * quantity * 100) : 0;
    useEffect(() => {
        if (!discount) return;
        // Re-validate the applied code against the new subtotal
        api.post("/discount-codes/validate", {
            code: discount.code, subtotal_cents: subtotalCents, applies_to: "tickets",
        }).then((r) => setDiscount(r.data)).catch(() => {
            setDiscount(null);
            toast.error("Promo no longer valid for this order. Removed.");
        });
    }, [subtotalCents]);

    const applyPromo = async () => {
        if (!promo.trim() || !selectedTier) {
            toast.error("Select a tier first, then enter a code.");
            return;
        }
        setApplying(true);
        try {
            const { data } = await api.post("/discount-codes/validate", {
                code: promo.trim(), subtotal_cents: subtotalCents, applies_to: "tickets",
            });
            setDiscount(data);
            toast.success(`Code applied: -$${(data.discount_cents / 100).toFixed(2)}`);
        } catch (err) {
            setDiscount(null);
            toast.error(formatErr(err));
        } finally {
            setApplying(false);
        }
    };

    const removePromo = () => { setPromo(""); setDiscount(null); };

    const checkout = async (e) => {
        e.preventDefault();
        if (!selectedTier) {
            toast.error("Select a tier first");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/tickets/checkout", {
                tier_id: selectedTier.id,
                quantity,
                buyer_name: buyer.name,
                buyer_email: buyer.email,
                buyer_phone: buyer.phone || null,
                game_id: gameId || null,
                discount_code: discount?.code || null,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(formatErr(err));
            setSubmitting(false);
        }
    };

    const discountCents = discount?.discount_cents || 0;
    const totalCents = Math.max(0, subtotalCents - discountCents);
    const total = (totalCents / 100).toFixed(2);
    const subtotalFmt = (subtotalCents / 100).toFixed(2);
    const discountFmt = (discountCents / 100).toFixed(2);

    return (
        <div data-testid="tickets-page" className="grain">
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4">Step Inside The Kingdom</div>
                    <h1 className="h-display text-6xl md:text-8xl">Buy Tickets</h1>
                    <p className="text-ink text-lg mt-5 max-w-xl">Single-game seats, season passes, and royal box experiences.</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
                <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-6">01 · Pick Your Tier</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {tiers.map((t, i) => {
                        const active = selectedTier?.id === t.id;
                        return (
                            <motion.button
                                key={t.id}
                                onClick={() => setSelectedTier(t)}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.06 }}
                                data-testid={`tier-${i}`}
                                className={`p-6 text-left border transition-all relative ${active ? "border-gold bg-purple-dark" : "card-flat"}`}
                            >
                                {t.is_season_pass && (
                                    <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-gold font-bold flex items-center gap-1">
                                        <Crown size={12} /> Season
                                    </div>
                                )}
                                <Ticket className={active ? "text-gold-bright" : "text-gold"} size={22} />
                                <div className="h-display text-xl mt-4">{t.name}</div>
                                <div className="h-display text-4xl text-gold mt-3 leading-none">${t.price_usd.toFixed(0)}</div>
                                {t.description && <div className="text-ink text-xs mt-3 leading-relaxed">{t.description}</div>}
                                {active && <div className="mt-4 text-[10px] tracking-widest uppercase text-gold font-bold">Selected</div>}
                            </motion.button>
                        );
                    })}
                </div>

                {tiers.length === 0 && (
                    <div className="card-flat p-12 text-center text-ink mt-6">No ticket options published yet.</div>
                )}

                {/* Step 2 — Buyer details */}
                <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-6 mt-16">02 · Your Details</div>
                <form onSubmit={checkout} data-testid="tickets-form" className="grid md:grid-cols-2 gap-10">
                    <div className="space-y-4">
                        <Field testid="tk-name" label="Full Name" required>
                            <input data-testid="tk-name" required value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <Field testid="tk-email" label="Email" required>
                            <input data-testid="tk-email" required type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <Field testid="tk-phone" label="Phone (Optional)">
                            <input data-testid="tk-phone" value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        {!selectedTier?.is_season_pass && games.length > 0 && (
                            <Field testid="tk-game" label="Select Game (Optional)">
                                <select value={gameId} onChange={(e) => setGameId(e.target.value)} className="w-full px-4 py-3 text-sm">
                                    <option value="">No specific game / open</option>
                                    {games.map((g) => (
                                        <option key={g.id} value={g.id}>
                                            {new Date(g.date).toLocaleDateString("en-US", { month: "short", day: "2-digit" })} — {g.home_away === "home" ? "vs." : "@"} {g.opponent}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        )}
                        <Field testid="tk-qty" label="Quantity">
                            <div className="flex items-center gap-3 mt-2">
                                <button type="button" data-testid="tk-qty-minus" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="btn-ghost-gold p-2"><Minus size={14} /></button>
                                <div className="h-display text-2xl w-12 text-center" data-testid="tk-qty-value">{quantity}</div>
                                <button type="button" data-testid="tk-qty-plus" onClick={() => setQuantity((q) => Math.min(20, q + 1))} className="btn-ghost-gold p-2"><Plus size={14} /></button>
                            </div>
                        </Field>
                    </div>

                    {/* Order summary */}
                    <aside className="card-flat p-6 h-fit" data-testid="tk-summary">
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase font-bold mb-4">Order Summary</div>
                        {selectedTier ? (
                            <>
                                <Row label="Tier" value={selectedTier.name} />
                                <Row label="Price" value={`$${selectedTier.price_usd.toFixed(2)}`} />
                                <Row label="Quantity" value={`${quantity}`} />
                                {gameId && !selectedTier.is_season_pass && (
                                    <Row label="Game" value={(games.find((g) => g.id === gameId)?.opponent) || "—"} />
                                )}
                                <div className="border-t border-line my-3" />
                                <Row label="Subtotal" value={`$${subtotalFmt}`} />
                                {discount && (
                                    <Row
                                        label={`Discount (${discount.code})`}
                                        value={<span data-testid="tk-row-discount" className="text-gold">{`-$${discountFmt}`}</span>}
                                    />
                                )}
                                <div className="border-t border-line my-3" />
                                <Row label={<span className="text-gold">Total</span>} value={<span className="h-display text-3xl text-gold" data-testid="tk-total">${total}</span>} large />

                                {/* Promo code */}
                                <div className="border-t border-line pt-4 mt-4">
                                    <label className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">
                                        <Tag size={12} /> Promo Code
                                    </label>
                                    {discount ? (
                                        <div className="flex items-center gap-2">
                                            <span data-testid="tk-promo-applied" className="text-gold text-xs tracking-widest font-bold uppercase">✓ {discount.code}</span>
                                            <button type="button" onClick={removePromo} data-testid="tk-promo-remove" className="ml-auto text-ink text-xs hover:text-red-400">Remove</button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={promo}
                                                onChange={(e) => setPromo(e.target.value.toUpperCase())}
                                                placeholder="KINGS10"
                                                data-testid="tk-promo-input"
                                                className="flex-1 px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none uppercase tracking-widest"
                                            />
                                            <button type="button" onClick={applyPromo} disabled={applying || !promo.trim()} data-testid="tk-promo-apply" className="btn-ghost-gold px-3 py-2 text-xs">
                                                {applying ? "…" : "Apply"}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <button type="submit" disabled={submitting || !buyer.name || !buyer.email} data-testid="tk-pay-btn" className="btn-gold w-full mt-6 py-4 text-xs disabled:opacity-50 inline-flex items-center justify-center gap-2">
                                    <CreditCard size={16} /> {submitting ? "Redirecting…" : `Checkout · $${total}`}
                                </button>
                                <p className="text-[10px] text-ink mt-3 uppercase tracking-widest">Payment secured by Stripe</p>
                            </>
                        ) : (
                            <p className="text-ink text-sm">Select a tier to see your total.</p>
                        )}
                    </aside>
                </form>
            </section>
        </div>
    );
}

function Field({ label, required, children, testid }) {
    return (
        <label className="block" data-testid={`field-${testid}`}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">
                {label} {required && <span className="text-gold">*</span>}
            </div>
            {children}
        </label>
    );
}

function Row({ label, value, large }) {
    return (
        <div className="flex justify-between items-center py-1.5">
            <div className={`text-xs uppercase tracking-widest ${large ? "" : "text-ink"}`}>{label}</div>
            <div className={large ? "" : "text-sm"}>{value}</div>
        </div>
    );
}
